import {
  computed,
  nextTick,
  onUnmounted,
  ref,
  type ComputedRef,
  type Ref,
} from 'vue'

type UseDropdownOptions = {
  /**
   * The root element of the dropdown. The composable will use this element to query the list items.
   */
  root: Ref<HTMLUListElement | null>
}

export type UseDropdown = {
  // states
  /**
   * The id of the currently activated menu item
   */
  activatedMenuItemId: Ref<string>

  /**
   * The root element of the dropdown. The composable will use this element to query the list items.
   */
  root: Ref<HTMLUListElement | null>

  /**
   * Whether the dropdown is open or not
   */
  isOpen: Ref<boolean>

  /**
   * The list items of the dropdown
   */
  listItems: Ref<NodeListOf<HTMLLIElement> | null>

  /**
   * The index of the currently active item
   */
  activeIndex: Ref<number>

  /**
   * The number of list items
   */
  listItemsCount: ComputedRef<number>

  // methods
  /**
   * Method responsible for loading the list items. This method should be executed after the dropdown is mounted.
   * @returns void
   */
  loadListItems: () => void

  /**
   * Update the tabindex of the list items based on the index
   * @param index number
   * @returns void
   */
  updateTabIndex: (index: number) => void

  /**
   * Method that focuses on the first item of the dropdown
   * @returns void
   */
  focusOnFirstItem: () => void

  /**
   * Method that focuses on the last item of the dropdown
   * @returns void
   */
  focusOnLastItem: () => void

  /**
   * Method that handles the keydown event on the list items. This method should be use in the keydown event listener of the element that contains the list SbDropdownItem.
   * @param event KeyboardEvent
   * @param options Object
   * @param options.onEscape Function
   * @param options.onKeydown Function
   * @param options.onClose Function
   * @returns
   * @returns void
   */
  handleKeydownOnList: (
    event: KeyboardEvent,
    options?: {
      onEscape?: (event: KeyboardEvent) => void
      onKeydown?: (event: KeyboardEvent) => void
      onClose?: (event: KeyboardEvent) => void
    },
  ) => void
}

export const useDropdown = (options: UseDropdownOptions): UseDropdown => {
  const { root } = options
  const activatedMenuItemId = ref('')
  const listItems = ref<NodeListOf<HTMLLIElement> | null>(null)
  const activeIndex = ref(-1)
  const isOpen = ref(false)
  const listItemsCount = computed(() => listItems.value?.length || 0)
  const loadListItems = (): void => {
    if (root.value) {
      listItems.value = root.value.querySelectorAll('.sb-dropdown-item')
    }
  }
  const updateTabIndex = (index: number): void => {
    activeIndex.value = index
    if (listItemsCount.value > 0 && listItems.value) {
      const nodeAtIndex = listItems.value[index]
      activatedMenuItemId.value = nodeAtIndex?.id || ''
      nodeAtIndex?.scrollIntoView({
        block: 'center',
      })
      nodeAtIndex?.focus()
    }
  }

  const focusOnFirstItem = (): void => {
    if (!isOpen.value) {
      isOpen.value = true
    }
    nextTick(() => {
      updateTabIndex(0)
    })
  }

  const focusOnLastItem = (): void => {
    if (!isOpen.value) {
      isOpen.value = true
    }
    nextTick(() => {
      updateTabIndex(listItemsCount.value - 1)
    })
  }

  const handleKeydownOnList = (
    event: KeyboardEvent,
    options?: {
      onEscape?: (event: KeyboardEvent) => void
      onKeydown?: (event: KeyboardEvent) => void
      onClose?: (event: KeyboardEvent) => void
    },
  ): void => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        event.stopPropagation()
        updateTabIndex((activeIndex.value + 1) % listItemsCount.value)
        break
      case 'ArrowUp':
        event.preventDefault()
        event.stopPropagation()
        updateTabIndex(
          (activeIndex.value - 1 + listItemsCount.value) % listItemsCount.value,
        )
        break
      case 'Home':
        focusOnFirstItem()
        break
      case 'End':
        focusOnLastItem()
        break
      case 'Tab':
        options?.onClose && options?.onClose(event)
        break
      case 'Escape':
        options?.onEscape && options?.onEscape(event)
        break
      default:
        break
    }
    options?.onKeydown && options?.onKeydown(event)
  }

  onUnmounted(() => {
    listItems.value = null
  })

  return {
    activatedMenuItemId,
    root,
    isOpen,
    listItems,
    activeIndex,
    listItemsCount,
    loadListItems,
    updateTabIndex,
    focusOnFirstItem,
    focusOnLastItem,
    handleKeydownOnList,
  }
}
